import React, { createContext, useEffect, useRef, useState } from 'react';
import io, { Socket } from 'socket.io-client';

interface WebsocketSendMessage {
  action: WebsocketActions;
  value?: any;
}

interface WebsocketProps {
  send: (message: WebsocketSendMessage) => void;
  joinToRoom: (roomName: string) => void;
  ws?: Socket;
}

export const WebsocketContext = createContext<WebsocketProps>({
  send: () => undefined,
  joinToRoom: (roomName: string) => undefined,
  ws: undefined,
});

interface WebsocketProviderProps {
  formId?: string;
  children: React.ReactNode;
}

export enum WebsocketActions {
  // přihlášení klienta
  CLIENT_LOGGED = 'clientLogged',
  CLIENT_LOGGED_INVALID = 'clientLoggedInvalid',
  // změna aktuálního kroku formuláře
  STEP_CHANGE = 'stepChange',
  // změna typu meetingu (online/offline)
  MEETING_MODE_CHANGE = 'meetingModeChange',
  // TODO: nevím, dospecifikovat
  CLIENT_AGREEMENT_CHECKED = 'clientAgreementChecked',
  // změna dat smlouvy
  CONTRACT_DATA_CHANGE = 'contractDataChange',
  // změna stavu zaškrtnutí souhlasů
  CONTRACT_AGREEEMENT_CHECKED = 'contractAgreementChecked',
  // otevření nebo zavření modalu s partnerem
  CONTRACT_PARTNER_FORM_OPEN = 'contractPartnerFormOpen',
  // změna dat partnera smlouvy
  CONTRACT_PARTNER_CHANGE = 'contractPartnerChange',
  // žádost o aktualizaci stavu smlouvy (pro podpisy, nahrané občanky)
  REFRESH_CONTRACT_STATE = 'refreshContractState',
  // žádost o aktualizaci nahraného souboru s občankou
  REFRESH_CONTRACT_IDENTITY_CARD_FILE = 'refreshContractIdentityCardFile',
  // žádost o aktualizaci předávacího protokolu
  REFRESH_CONTRACT_TRANSMIT_REPORT = 'refreshContractTransmitReport',
}

export const WebsocketProvider = ({ formId, children }: WebsocketProviderProps) => {
  const [room, setRoom] = useState<string>('');

  const ws = useRef<Socket>();

  const webSocketListener = (newMessage: string) => {
    // TODO: globální akce
    // console.log('webSocketListener message', newMessage);
  };

  const joinToRoom = (roomName: string) => {
    if (room) {
      ws.current?.emit('leaveRoom', room);
    }
    ws.current?.emit('joinToRoom', roomName);
    setRoom(roomName);
  };

  const send = (message: WebsocketSendMessage) => {
    if (ws.current) {
      // console.log('WebsocketProvider send', message);
      ws.current.emit(message.action || 'message', {
        room,
        message: message.value,
      });
    }
  };

  useEffect(() => {
    if (process.env.REACT_APP_WEBSOCKET_URL) {
      const socket = io(process.env.REACT_APP_WEBSOCKET_URL);
      socket.on('message', webSocketListener);
      ws.current = socket;
      return () => {
        socket.close();
      };
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (ws.current && formId) {
      joinToRoom(formId);
    }
    // eslint-disable-next-line
  }, [ws.current, formId]);

  const ret = {
    send,
    joinToRoom,
    ws: ws.current,
  };

  return <WebsocketContext.Provider value={ret}>{children}</WebsocketContext.Provider>;
};
